import { FestivalData } from '../model/FestivalData';
import { Language } from '../i18n/language';

export type AgentSection = 'infos' | 'artists' | 'shows';
export interface AgentMessage {
  role: 'user' | 'assistant';
  content: string;
}
export interface AgentPatch {
  op: 'add' | 'replace' | 'remove';
  path: string;
  value?: unknown;
}
export interface AgentReply {
  message: string;
  patches: AgentPatch[];
}

export const boundAgentHistory = (messages: AgentMessage[]): AgentMessage[] => {
  const recent = [...messages];
  while (recent.length > 11 || recent.reduce((total, message) => total + message.content.length, 0) > 12000) recent.splice(0, 2);
  return recent;
};

export class AgentPatchError extends Error {
  constructor(public readonly reason: 'invalid' | 'stale') {
    super(reason);
  }
}

export class AgentRequestError extends Error {
  constructor(public readonly reason: 'unavailable' | 'limit' | 'invalid' | 'session' | 'permission' | 'draft' | 'failed') {
    super(reason);
  }
}

const owns = (value: object, key: string): boolean => Object.prototype.hasOwnProperty.call(value, key);
const object = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);
const forbiddenKeys = ['__proto__', 'prototype', 'constructor', 'file'];

const styleFields: Record<string, string[]> = {
  '': ['title_color', 'caption_color', 'body_color', 'nav_bar_logo_height', 'festival_name_color'],
  background: ['type', 'primary_color', 'secondary_color', 'image_opacity'],
  show: ['cell_height', 'details_header_height', 'items_artist_name_color', 'items_scene_name_color', 'items_show_date_color', 'title_color', 'buttons_tint_color', 'items_layout', 'items_tag_background_color', 'items_tag_text_color'],
  news: ['cell_height', 'padding', 'header_height', 'items_spacing', 'items_background_color', 'items_title_color', 'items_subtitle_color', 'items_date_color', 'details_header_height', 'title_color'],
  pages: ['icon_height', 'header_height', 'row_height', 'title_color', 'section_color', 'section_height', 'icon_tint_color'],
  notifications: ['padding', 'items_spacing', 'items_vertical_spacing'],
  tab_bar: ['selected_color', 'background_color', 'background_opacity'],
  empty_show_list: ['buy_ticket_button_text_color', 'buy_ticket_button_background_color', 'title_color'],
  tickets: ['buy_ticket_button_text_color', 'buy_ticket_button_background_color'],
};
const artistFields = ['name', 'description', 'style'];
const musicFields = ['spotify_url', 'apple_music_url', 'youtube_url', 'soundcloud_url', 'other_url'];
const showFields = ['scene_id', 'artist_id', 'start_date', 'end_date', 'featured'];
const homepageFields = ['enabled', 'start_date', 'end_date', 'before_message', 'after_message'];

const invalid = (): never => { throw new AgentPatchError('invalid'); };
const stale = (): never => { throw new AgentPatchError('stale'); };

export const agentPatchSegments = (patch: AgentPatch): string[] => {
  if (!patch || !['add', 'replace', 'remove'].includes(patch.op) || typeof patch.path !== 'string'
    || !patch.path.startsWith('/') || (patch.op !== 'remove' && !owns(patch, 'value'))) return invalid();
  const raw = patch.path.slice(1).split('/');
  if (raw.some(part => /~(?![01])/u.test(part))) return invalid();
  const parts = raw.map(part => part.replace(/~1/g, '/').replace(/~0/g, '~'));
  if (parts.some(part => !part || forbiddenKeys.includes(part))) return invalid();
  const [section, group, field, leaf] = parts;
  if (section === 'infos') {
    if (parts.length === 2 && ['festival_name', 'lineup_release_date', 'show_reminder_minutes', 'homepage'].includes(group)) return parts;
    if (group === 'homepage' && parts.length === 3 && homepageFields.includes(field)) return parts;
    if (group === 'tickets' && parts.length === 3 && ['description', 'link'].includes(field)) return parts;
    if (group === 'stylesheet') {
      if (parts.length === 3 && (styleFields[''].includes(field) || field === 'background')) return parts;
      if (parts.length === 4 && owns(styleFields, field) && styleFields[field].includes(leaf)) return parts;
    }
  }
  if (section === 'artists' || section === 'shows') {
    if (!/^(0|[1-9]\d*|-)$/.test(group)) return invalid();
    if (parts.length === 2 && ['add', 'remove'].includes(patch.op) && (group !== '-' || patch.op === 'add')) return parts;
    if (group === '-') return invalid();
    if (parts.length === 3 && (section === 'artists' ? artistFields : showFields).includes(field)) return parts;
    if (section === 'artists' && parts.length === 4 && field === 'music_preview' && musicFields.includes(leaf)) return parts;
  }
  return invalid();
};

const safeValue = (value: unknown): boolean => {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return true;
  if (typeof value === 'number') return Number.isFinite(value);
  if (value instanceof File) return true;
  if (Array.isArray(value)) return value.every(safeValue);
  return object(value) && Object.getPrototypeOf(value) === Object.prototype
    && Object.entries(value).every(([key, child]) => key === 'file' ? child instanceof File : !forbiddenKeys.includes(key) && safeValue(child));
};

const readAt = (data: unknown, parts: string[]): unknown => {
  let value = data;
  for (const part of parts) {
    if (Array.isArray(value)) value = value[Number(part)];
    else if (object(value) && owns(value, part)) value = value[part];
    else return undefined;
  }
  return value;
};

const equal = (before: unknown, after: unknown): boolean => {
  if (before === after) return true;
  // Unsaved image files must compare by identity, and survive unrelated edits intact.
  if (before instanceof Blob || after instanceof Blob) return false;
  if (Array.isArray(before) && Array.isArray(after)) {
    return before.length === after.length && before.every((value, index) => equal(value, after[index]));
  }
  if (object(before) && object(after)) {
    return Object.keys(before).length === Object.keys(after).length
      && Object.keys(before).every(key => owns(after, key) && equal(before[key], after[key]));
  }
  return false;
};

const updateAt = (node: unknown, parts: string[], patch: AgentPatch): unknown => {
  const [key, ...rest] = parts;
  if (Array.isArray(node)) {
    if (!/^(0|[1-9]\d*|-)$/.test(key)) return invalid();
    const index = key === '-' ? node.length : Number(key);
    if (index > node.length || (index === node.length && (rest.length > 0 || patch.op !== 'add'))) return invalid();
    const copy = [...node];
    if (rest.length) copy[index] = updateAt(node[index], rest, patch);
    else if (patch.op === 'add') copy.splice(index, 0, patch.value);
    else if (patch.op === 'remove') copy.splice(index, 1);
    else copy[index] = patch.value;
    return copy;
  }
  if (!object(node) || (!owns(node, key) && (rest.length > 0 || patch.op !== 'add'))) return invalid();
  const copy = { ...node };
  if (rest.length) copy[key] = updateAt(node[key], rest, patch);
  else if (patch.op === 'remove') delete copy[key];
  else copy[key] = patch.value;
  return copy;
};

const sameOrder = (before: FestivalData, current: FestivalData, section: 'artists' | 'shows'): boolean =>
  equal(before[section].map(item => item.identifier), current[section].map(item => item.identifier));

/** Applies only the reviewed fields to the latest editor state, retaining unrelated edits and File objects. */
export const applyAgentPatches = (snapshot: FestivalData, current: FestivalData, patches: AgentPatch[]): FestivalData => {
  if (!Array.isArray(patches) || patches.length > 400) return invalid();
  if (snapshot.version !== current.version || snapshot.status !== 'draft' || current.status !== 'draft') return stale();
  let before = snapshot;
  let next = current;
  for (const patch of patches) {
    const parts = agentPatchSegments(patch);
    if (patch.op !== 'remove' && !safeValue(patch.value)) return invalid();
    if (parts[0] === 'artists' || parts[0] === 'shows') {
      if (!sameOrder(before, next, parts[0])) return stale();
      // Resolving artists and stages used the request's lineup context.
      if (parts[0] === 'shows' && (!sameOrder(before, next, 'artists') || !equal(before.pois, next.pois))) return stale();
    }
    if (parts[0] === 'infos' && parts[1] === 'stylesheet' && parts[2] === 'background') {
      if (!equal(before.infos.stylesheet.background, next.infos.stylesheet.background)) return stale();
      if (parts.length === 3 && before.infos.stylesheet.background.type === 'Image') return invalid();
    }
    const parent = readAt(before, parts.slice(0, -1));
    const insertsIntoArray = patch.op === 'add' && Array.isArray(parent);
    if (!insertsIntoArray && !equal(readAt(before, parts), readAt(next, parts))) return stale();
    before = updateAt(before, parts, patch) as FestivalData;
    next = updateAt(next, parts, patch) as FestivalData;
  }
  return next;
};

/** Produces a field-scoped undo, so manual edits made after Apply are preserved. */
export const invertAgentPatches = (snapshot: FestivalData, patches: AgentPatch[]): AgentPatch[] => {
  let before = snapshot;
  const inverse: AgentPatch[] = [];
  for (const patch of patches) {
    const parts = agentPatchSegments(patch);
    const parent = readAt(before, parts.slice(0, -1));
    const previous = readAt(before, parts);
    const path = parts[parts.length - 1] === '-' && Array.isArray(parent)
      ? `${patch.path.slice(0, -1)}${parent.length}` : patch.path;
    if (patch.op === 'remove') inverse.unshift({ op: 'add', path, value: previous });
    else if (patch.op === 'add' && (Array.isArray(parent) || previous === undefined)) inverse.unshift({ op: 'remove', path });
    else inverse.unshift({ op: 'replace', path, value: previous });
    before = updateAt(before, parts, patch) as FestivalData;
  }
  return inverse;
};

export const agentPatchValues = (snapshot: FestivalData, patches: AgentPatch[]): { patch: AgentPatch; before: unknown; after: unknown; context: FestivalData }[] => {
  let before = snapshot;
  return patches.map(patch => {
    const parts = agentPatchSegments(patch);
    const previous = readAt(before, parts);
    const context = before;
    before = updateAt(before, parts, patch) as FestivalData;
    return { patch, before: previous, after: patch.op === 'remove' ? undefined : patch.value, context };
  });
};

/** Whether the API has the assistant set up (api/FESTIVAL_AGENT.md); any failure counts as not */
export const fetchFestivalAgentEnabled = async (sessionToken: string, festivalId: string): Promise<boolean> => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/festival/agent`, {
      headers: { 'X-Festival-Id': festivalId, 'X-Session-Token': sessionToken },
    });
    return response.ok && (await response.json()).data?.enabled === true;
  } catch {
    return false;
  }
};

export const requestFestivalAgent = async (
  sessionToken: string,
  festivalId: string,
  festivalData: FestivalData,
  section: AgentSection,
  messages: AgentMessage[],
  language: Language,
  signal: AbortSignal,
): Promise<AgentReply> => {
  const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/festival/agent/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Festival-Id': festivalId,
      'X-Session-Token': sessionToken,
    },
    body: JSON.stringify({
      festival_data: festivalData,
      section,
      messages: boundAgentHistory(messages),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language,
    }, (key, value) => key === 'file' ? undefined : value),
    signal,
  });
  if (!response.ok) {
    if (response.status === 503) throw new AgentRequestError('unavailable');
    if (response.status === 429) throw new AgentRequestError('limit');
    if ([400, 413].includes(response.status)) throw new AgentRequestError('invalid');
    if (response.status === 401) throw new AgentRequestError('session');
    if (response.status === 403) throw new AgentRequestError('permission');
    if (response.status === 409) throw new AgentRequestError('draft');
    throw new AgentRequestError('failed');
  }
  const envelope = await response.json();
  const result: unknown = envelope.data;
  if (!object(result) || typeof result.message !== 'string' || !Array.isArray(result.patches)) return invalid();
  return { message: result.message, patches: result.patches };
};
